<template>
  <div
    class="flex flex-col lg:flex-row w-full h-[calc(100vh-2rem)] gap-6 p-2 sm:p-4 font-sans text-zinc-900 dark:text-zinc-100 bg-zinc-50 dark:bg-[#09090b] overflow-hidden transition-colors duration-500">

    <aside
      class="w-full lg:w-[340px] flex flex-col bg-white/90 dark:bg-[#121212]/90 backdrop-blur-xl rounded-3xl border border-zinc-200/80 dark:border-white/5 shadow-2xl shadow-zinc-200/50 dark:shadow-black/50 overflow-hidden shrink-0 z-30 h-full transition-all duration-500">

      <div class="p-6 border-b border-zinc-100 dark:border-white/5 shrink-0">
        <div class="flex items-center justify-between mb-2">
          <h2 class="text-xs font-bold uppercase tracking-widest text-zinc-400 dark:text-zinc-500">Design Tokens</h2>

          <div class="flex gap-2">
            <router-link to="/dashboard/hub"
              class="group p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-white/5 text-zinc-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all active:scale-95"
              title="Tutorial Hub">
              <span
                class="material-symbols-rounded text-lg transition-transform duration-300 group-hover:rotate-12">school</span>
            </router-link>

            <button @click="randomize"
              class="p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-white/5 text-zinc-400 transition-colors active:scale-95"
              title="Randomize">
              <span class="material-symbols-rounded text-lg">shuffle</span>
            </button>

            <button @click="reset"
              class="p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-white/5 text-zinc-400 transition-colors active:scale-95"
              title="Reset">
              <span class="material-symbols-rounded text-lg">replay</span>
            </button>
          </div>
        </div>
        <p class="text-xs text-zinc-500 dark:text-zinc-400">System values</p>
      </div>

      <div class="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-5">
        <div v-for="(color, index) in palette" :key="index" class="group">
          <div class="flex items-center justify-between mb-2">
            <label class="text-xs font-semibold text-zinc-700 dark:text-zinc-200">{{ color.role }}</label>
            <span class="text-[10px] text-zinc-400">{{ color.usage }}</span>
          </div>

          <div
            class="flex items-center gap-3 p-1.5 rounded-xl bg-zinc-50 dark:bg-black/20 border border-zinc-200 dark:border-white/5 transition-all hover:border-zinc-300 dark:hover:border-white/10 focus-within:border-indigo-500/50 focus-within:ring-1 focus-within:ring-indigo-500/50 shadow-sm">

            <div
              class="relative w-10 h-10 rounded-lg shadow-inner ring-1 ring-black/5 dark:ring-white/10 overflow-hidden cursor-pointer shrink-0 group/picker transition-transform active:scale-95">
              <input type="color" v-model="color.hex"
                class="absolute inset-0 w-[200%] h-[200%] -top-1/2 -left-1/2 opacity-0 cursor-pointer z-20">
              <div class="absolute inset-0 transition-colors duration-300" :style="{ backgroundColor: color.hex }">
              </div>
              <div
                class="absolute inset-0 flex items-center justify-center opacity-0 group-hover/picker:opacity-100 bg-black/20 transition-opacity pointer-events-none z-10">
                <span class="material-symbols-rounded text-white text-sm drop-shadow-md">colorize</span>
              </div>
            </div>

            <div class="flex-1 px-1">
              <input type="text" v-model="color.hex"
                class="w-full bg-transparent border-none p-0 text-xs font-mono font-medium text-zinc-700 dark:text-zinc-200 focus:ring-0 uppercase tracking-wide"
                maxlength="7">
            </div>
          </div>
        </div>

        <div
          class="p-4 rounded-xl bg-zinc-100/50 dark:bg-white/5 border border-zinc-200/50 dark:border-white/5 backdrop-blur-sm mt-4">
          <div class="flex items-center justify-between mb-3">
            <div class="flex flex-col">
              <span class="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Contrast</span>
              <span class="text-[10px] text-zinc-400 font-mono">{{ contrastData.ratio }}:1</span>
            </div>
            <span class="text-[10px] font-bold px-2 py-0.5 rounded border transition-colors duration-300"
              :class="contrastData.colorClass">
              {{ contrastData.label }}
            </span>
          </div>
          <div class="flex gap-1 h-1.5 w-full rounded-full overflow-hidden bg-zinc-200 dark:bg-white/10">
            <div class="h-full rounded-full transition-all duration-500 ease-out" :class="contrastData.barColor"
              :style="{ width: `${contrastData.percent}%` }"></div>
          </div>
        </div>
      </div>
    </aside>

    <main
      class="flex-1 min-w-0 relative rounded-3xl overflow-hidden bg-zinc-100 dark:bg-[#050505] border border-zinc-200 dark:border-white/5 shadow-2xl flex flex-col h-full group/main">

      <div class="absolute inset-0 pointer-events-none overflow-hidden transition-opacity duration-1000">
        <div
          class="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] rounded-full blur-[120px] mix-blend-multiply dark:mix-blend-screen opacity-10 dark:opacity-15 transition-colors duration-[1500ms]"
          :style="{ backgroundColor: palette[0].hex }"></div>
        <div
          class="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] rounded-full blur-[100px] mix-blend-multiply dark:mix-blend-screen opacity-10 dark:opacity-15 transition-colors duration-[1500ms]"
          :style="{ backgroundColor: palette[2].hex }"></div>
        <div
          class="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03] dark:opacity-[0.05] mix-blend-overlay">
        </div>
      </div>

      <div class="relative z-10 p-6 flex justify-center shrink-0">
        <div
          class="flex items-center p-1.5 rounded-2xl bg-white/60 dark:bg-zinc-900/60 border border-zinc-200/60 dark:border-white/5 backdrop-blur-md shadow-sm transition-all hover:shadow-md">
          <button v-for="scene in scenes" :key="scene.id" @click="activeScene = scene.id"
            class="px-5 py-2 rounded-xl text-xs font-medium transition-all duration-300 flex items-center gap-2.5 whitespace-nowrap relative overflow-hidden"
            :class="activeScene === scene.id ? 'bg-white dark:bg-[#202022] text-zinc-900 dark:text-white shadow-md ring-1 ring-black/5 dark:ring-white/10' : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200 hover:bg-black/5 dark:hover:bg-white/5'">
            <span class="material-symbols-rounded text-lg transition-transform duration-300 group-hover:scale-110">{{
              scene.icon }}</span>
            {{ scene.label }}
          </button>
        </div>
      </div>

      <div
        class="relative z-10 flex-1 w-full overflow-auto custom-scrollbar flex flex-col items-start p-4 lg:p-6 perspective-[1000px]">

        <div class="w-full h-full flex flex-col min-h-0">

          <transition name="fade" mode="out-in">

            <div v-if="activeScene === 'dashboard'" key="dash"
              class="w-full min-w-[1000px] h-full bg-white dark:bg-[#0c0c0e] rounded-2xl border border-zinc-200 dark:border-white/10 shadow-2xl flex overflow-hidden ring-1 ring-black/5 dark:ring-white/5 transition-all duration-500 shrink-0 mx-auto"
              style="transform: scale(0.8); transform-origin: top center;">

              <div
                class="w-20 lg:w-72 border-r border-zinc-100 dark:border-white/5 bg-zinc-50/80 dark:bg-[#111113] flex flex-col p-6 backdrop-blur-xl shrink-0 transition-all duration-500">
                <div class="flex items-center gap-4 mb-10 justify-center lg:justify-start">
                  <div class="w-8 h-8 rounded-xl shrink-0 transition-colors duration-500 shadow-md"
                    :style="{ background: `linear-gradient(135deg, ${palette[0].hex}, ${palette[1].hex})` }"></div>
                  <div class="hidden lg:block h-3 w-28 rounded-full bg-zinc-200 dark:bg-white/10"></div>
                </div>
                <div class="space-y-3">
                  <div v-for="i in 5" :key="i"
                    class="h-12 rounded-xl flex items-center justify-center lg:justify-start px-0 lg:px-4 gap-4 cursor-pointer transition-all duration-200 group"
                    :class="i === 1 ? 'bg-white dark:bg-white/10 shadow-sm ring-1 ring-black/5 dark:ring-white/5' : 'hover:bg-zinc-100 dark:hover:bg-white/5'">
                    <div class="w-5 h-5 rounded-lg bg-zinc-300 dark:bg-white/20 transition-colors shrink-0"
                      :style="i === 1 ? { backgroundColor: palette[0].hex } : {}"></div>
                    <div
                      class="hidden lg:block h-2.5 w-24 rounded-full bg-zinc-200 dark:bg-white/10 group-hover:bg-zinc-300 dark:group-hover:bg-white/20 transition-colors">
                    </div>
                  </div>
                </div>
                <div
                  class="mt-auto p-5 rounded-2xl bg-zinc-100/50 dark:bg-white/5 border border-zinc-200/50 dark:border-white/5 hidden lg:block backdrop-blur-md">
                  <div class="flex items-center gap-3 mb-3">
                    <div class="w-9 h-9 rounded-full bg-zinc-300 dark:bg-white/10 shrink-0 shadow-inner"></div>
                    <div class="space-y-2">
                      <div class="h-2.5 w-20 bg-zinc-300 dark:bg-white/20 rounded-full"></div>
                      <div class="h-2 w-12 bg-zinc-200 dark:bg-white/10 rounded-full"></div>
                    </div>
                  </div>
                  <div class="h-1.5 w-full bg-zinc-200 dark:bg-black/40 rounded-full overflow-hidden mt-2">
                    <div class="h-full rounded-full transition-all duration-1000 ease-out"
                      :style="{ width: '75%', backgroundColor: palette[2].hex }"></div>
                  </div>
                </div>
              </div>

              <div class="flex-1 flex flex-col bg-white/50 dark:bg-transparent min-w-0">
                <header
                  class="h-20 border-b border-zinc-100 dark:border-white/5 flex items-center justify-between px-8 bg-white/80 dark:bg-[#0c0c0e]/80 backdrop-blur-md z-10 sticky top-0 shrink-0">
                  <div class="h-3 w-32 bg-zinc-200 dark:bg-white/10 rounded-full"></div>
                  <button
                    class="px-5 py-2.5 rounded-lg text-xs font-bold text-white shadow-lg shadow-indigo-500/20 transition-all duration-300 hover:scale-105 active:scale-95 uppercase tracking-wide"
                    :style="{ backgroundColor: palette[0].hex, color: palette[4].hex === '#FFFFFF' ? '#FFFFFF' : '#000000' }">Create
                    Report</button>
                </header>

                <div class="p-8 overflow-y-auto custom-scrollbar space-y-8 flex-1"
                  :style="{ backgroundColor: palette[3].hex }">
                  <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div v-for="(stat, idx) in stats" :key="idx"
                      class="p-6 rounded-2xl bg-white dark:bg-[#151518] border border-zinc-100 dark:border-white/5 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500 group ease-out min-h-[140px] flex flex-col justify-between">
                      <div class="flex justify-between items-start mb-5">
                        <div class="p-2.5 rounded-xl transition-colors duration-300 group-hover:scale-110"
                          :style="{ backgroundColor: palette[idx % 3].hex + '15', color: palette[idx % 3].hex }">
                          <span class="material-symbols-rounded text-xl">{{ stat.icon }}</span>
                        </div>
                        <span
                          class="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded-lg">+{{
                          stat.growth }}%</span>
                      </div>
                      <div>
                        <h3 class="text-3xl font-bold mb-1 tracking-tight transition-colors duration-300"
                          :style="{ color: palette[4].hex }">{{ stat.value }}</h3>
                        <p class="text-xs text-zinc-500 font-medium">{{ stat.label }}</p>
                      </div>
                    </div>
                  </div>

                  <div
                    class="min-h-[360px] rounded-2xl bg-white dark:bg-[#151518] border border-zinc-100 dark:border-white/5 p-8 shadow-sm flex flex-col relative overflow-hidden group/chart">
                    <div class="flex justify-between items-center mb-10 relative z-10">
                      <div class="h-3 w-48 bg-zinc-200 dark:bg-white/10 rounded-full"></div>
                      <div class="flex gap-3">
                        <div class="h-9 w-24 rounded-lg bg-zinc-100 dark:bg-white/5"></div>
                        <div class="h-9 w-9 rounded-lg bg-zinc-100 dark:bg-white/5"></div>
                      </div>
                    </div>
                    <div class="flex-1 flex items-end gap-3 z-10 px-2 pb-2">
                      <div v-for="bar in 24" :key="bar"
                        class="flex-1 rounded-t-[4px] opacity-80 hover:opacity-100 hover:scale-y-105 transition-all duration-500 ease-out origin-bottom cursor-pointer relative group/bar"
                        :style="{
                          height: `${Math.random() * 60 + 20}%`,
                          background: `linear-gradient(to top, ${palette[bar % 2].hex}, ${palette[bar % 2].hex}90)`
                        }">
                      </div>
                    </div>
                    <div class="absolute inset-0 bg-gradient-to-t from-white/5 to-transparent pointer-events-none">
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-else-if="activeScene === 'mobile'" key="mobile"
              class="w-full h-full flex items-center justify-center py-4 shrink-0">
              <div
                class="w-[360px] h-[720px] bg-[#f2f2f2] dark:bg-[#000] rounded-[60px] border-[12px] border-zinc-200 dark:border-[#1a1a1a] shadow-2xl relative shrink-0 transform transition-transform duration-700 hover:scale-[1.01] z-10">

                <div
                  class="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-7 bg-zinc-200 dark:bg-[#1a1a1a] rounded-b-3xl z-50 pointer-events-none">
                </div>

                <div
                  class="w-full h-full flex flex-col overflow-hidden relative rounded-[48px] bg-white dark:bg-[#000] z-0">

                  <div class="pt-14 px-6 pb-6 flex justify-between items-end bg-white dark:bg-[#000] shrink-0 z-10"
                    :style="{ backgroundColor: palette[3].hex }">
                    <div>
                      <p class="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-1">Total Balance</p>
                      <h2 class="text-3xl font-bold tracking-tighter" :style="{ color: palette[4].hex }">$24,500</h2>
                    </div>
                    <div
                      class="w-10 h-10 rounded-full bg-zinc-100 dark:bg-white/10 flex items-center justify-center transition-colors duration-300 hover:bg-zinc-200 dark:hover:bg-white/20">
                      <span class="material-symbols-rounded text-xl"
                        :style="{ color: palette[4].hex }">notifications</span>
                    </div>
                  </div>

                  <div class="flex-1 overflow-y-auto no-scrollbar pb-8 relative z-0"
                    :style="{ backgroundColor: palette[3].hex }">
                    <div class="px-4 mb-6">
                      <div
                        class="h-52 w-full rounded-[36px] p-6 relative overflow-hidden shadow-2xl shadow-indigo-500/20 group transition-all duration-700"
                        :style="{ background: `linear-gradient(135deg, ${palette[0].hex}, ${palette[1].hex})` }">
                        <div class="relative z-10 flex flex-col h-full justify-between"
                          :style="{ color: palette[4].hex === '#FFFFFF' ? '#FFFFFF' : '#000000' }">
                          <div class="flex justify-between items-start">
                            <div
                              class="px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-lg text-[9px] font-bold border border-white/10 tracking-widest shadow-sm">
                              PREMIUM</div>
                            <span class="material-symbols-rounded text-2xl drop-shadow-md">contactless</span>
                          </div>
                          <div>
                            <div class="flex gap-3 mb-2 font-mono opacity-90">
                              <span class="text-xl tracking-widest">••••</span>
                              <span class="text-xl tracking-widest">••••</span>
                              <span class="text-xl tracking-widest font-bold">4288</span>
                            </div>
                            <div class="flex justify-between text-[10px] font-medium opacity-80">
                              <span class="tracking-wider">GABRIEL MATOS</span>
                              <span>12/28</span>
                            </div>
                          </div>
                        </div>
                        <div
                          class="absolute -right-12 -top-12 w-56 h-56 rounded-full bg-white/10 blur-[60px] group-hover:scale-125 transition-transform duration-[2s]">
                        </div>
                        <div class="absolute -left-10 -bottom-10 w-40 h-40 rounded-full bg-black/10 blur-[40px]"></div>
                      </div>
                    </div>

                    <div class="px-4 flex gap-3 mb-8">
                      <div v-for="(action, i) in ['arrow_upward', 'add', 'more_horiz']" :key="i"
                        class="flex-1 flex flex-col items-center gap-2 group cursor-pointer">
                        <div
                          class="w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 group-hover:-translate-y-1 shadow-sm group-hover:shadow-md"
                          :style="{ backgroundColor: i === 2 ? (palette[3].hex === '#000000' ? '#1a1a1a' : '#f4f4f5') : palette[i + 1]?.hex + '15', color: i === 2 ? (palette[4].hex === '#FFFFFF' ? '#ffffff' : '#000000') : palette[i + 1]?.hex }">
                          <span
                            class="material-symbols-rounded text-2xl transition-transform duration-300 group-hover:scale-110">{{
                            action }}</span>
                        </div>
                        <span
                          class="text-[10px] font-medium text-zinc-600 dark:text-zinc-400 group-hover:text-zinc-900 dark:group-hover:text-zinc-200 transition-colors">{{
                            i === 0 ? 'Send' : i === 1 ? 'Topup' : 'More' }}</span>
                      </div>
                    </div>

                    <div
                      class="bg-white dark:bg-[#0c0c0e] rounded-t-[40px] p-6 space-y-6 shadow-[0_-10px_40px_rgba(0,0,0,0.03)] border-t border-zinc-50 dark:border-white/5 min-h-[300px]">
                      <div class="flex justify-between items-center">
                        <h3 class="text-base font-bold tracking-tight" :style="{ color: palette[4].hex }">Transactions
                        </h3>
                        <span
                          class="text-[10px] font-medium text-zinc-400 cursor-pointer hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors px-2 py-1 rounded-md hover:bg-zinc-100 dark:hover:bg-white/5">View
                          All</span>
                      </div>
                      <div v-for="t in 6" :key="t"
                        class="flex items-center justify-between group cursor-pointer hover:bg-zinc-50 dark:hover:bg-white/5 p-3 rounded-2xl transition-all duration-300 -mx-3 border border-transparent hover:border-zinc-100 dark:hover:border-white/5">
                        <div class="flex items-center gap-4">
                          <div
                            class="w-12 h-12 rounded-2xl bg-zinc-50 dark:bg-white/5 flex items-center justify-center text-xl group-hover:scale-105 transition-transform duration-300 border border-zinc-100 dark:border-white/5">
                            {{ t === 1 ? '🍎' : t === 2 ? '🎬' : t === 3 ? '🛍️' : '🍔' }}
                          </div>
                          <div>
                            <h4 class="text-sm font-bold mb-0.5" :style="{ color: palette[4].hex }">{{ t === 1 ? 'Apple    Store' : t === 2 ? 'Netflix' : t === 3 ? 'Shopping' : 'Uber Eats' }}</h4>
                            <p class="text-[10px] text-zinc-400 font-medium">Today, 10:{{ 15 + t }} AM</p>
                          </div>
                        </div>
                        <span class="text-sm font-bold font-mono tracking-tight" :style="{ color: palette[4].hex }">-${{
                          t * 20 }}.00</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-else-if="activeScene === 'typography'" key="type"
              class="w-full h-full flex items-center justify-center shrink-0 min-h-[600px] overflow-auto">
              <div
                class="w-full min-w-[900px] max-w-7xl grid grid-cols-1 xl:grid-cols-2 gap-12 p-6 items-center mx-auto"
                style="transform: scale(0.8); transform-origin: top center;">
                <div class="space-y-12">
                  <div class="group">
                    <p
                      class="text-[10px] font-bold font-mono text-zinc-400 mb-4 uppercase tracking-widest opacity-70 group-hover:opacity-100 transition-opacity">
                      Display / H1</p>
                    <h1
                      class="text-6xl lg:text-8xl font-black tracking-tighter leading-[0.95] transition-all duration-500 hover:scale-[1.02] origin-left"
                      :style="{ color: palette[0].hex }">
                      Visual<br>Impact.
                    </h1>
                  </div>
                  <div class="group">
                    <p
                      class="text-[10px] font-bold font-mono text-zinc-400 mb-4 uppercase tracking-widest opacity-70 group-hover:opacity-100 transition-opacity">
                      Heading / H2</p>
                    <h2
                      class="text-3xl lg:text-5xl font-bold tracking-tight leading-snug transition-colors duration-300"
                      :style="{ color: palette[4].hex }">
                      Refining the <span class="italic font-serif relative px-1"
                        :style="{ color: palette[2].hex }">aesthetic<span
                          class="absolute bottom-1 left-0 w-full h-[0.1em] bg-current opacity-30"></span></span> of
                      modern interfaces.
                    </h2>
                  </div>
                  <div class="group">
                    <p
                      class="text-[10px] font-bold font-mono text-zinc-400 mb-4 uppercase tracking-widest opacity-70 group-hover:opacity-100 transition-opacity">
                      Body / P</p>
                    <p class="text-base lg:text-lg leading-relaxed max-w-lg font-medium transition-colors duration-300 border-l-4 pl-6"
                      :style="{ color: palette[4].hex, borderColor: palette[1].hex }">
                      Color is not just a visual element; it's a language. It communicates hierarchy, state, and brand
                      identity instantaneously. Careful calibration ensures accessibility and beauty coexist.
                    </p>
                  </div>
                </div>

                <div
                  class="flex flex-col justify-center space-y-4 bg-white/5 dark:bg-black/20 p-8 rounded-[32px] border border-zinc-200 dark:border-white/5 backdrop-blur-sm shadow-xl">
                  <div v-for="c in palette" :key="c.id"
                    class="flex items-center gap-6 group p-4 hover:bg-zinc-50 dark:hover:bg-white/5 rounded-2xl transition-all duration-300 cursor-pointer border border-transparent hover:border-zinc-200 dark:hover:border-white/5">
                    <div
                      class="w-16 h-16 rounded-2xl shadow-lg ring-1 ring-black/5 dark:ring-white/10 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shrink-0 relative overflow-hidden"
                      :style="{ backgroundColor: c.hex }">
                      <div
                        class="absolute inset-0 bg-gradient-to-tr from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      </div>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex justify-between items-baseline mb-1.5">
                        <span class="font-bold text-lg tracking-tight" :style="{ color: palette[4].hex }">{{ c.name
                          }}</span>
                        <span
                          class="font-mono text-[10px] text-zinc-400 uppercase tracking-widest bg-zinc-100 dark:bg-white/10 px-2 py-1 rounded-md">{{
                          c.hex }}</span>
                      </div>
                      <p class="text-xs text-zinc-500 font-medium flex items-center gap-2">
                        <span class="w-1.5 h-1.5 rounded-full" :style="{ backgroundColor: c.hex }"></span>
                        {{ c.role }} Token
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </transition>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const activeScene = ref('dashboard')

const stats = [
  { label: 'Revenue', value: '$124.5K', growth: 12, icon: 'payments' },
  { label: 'Users', value: '45.2K', growth: 8, icon: 'group' },
  { label: 'Active', value: '1.2K', growth: 24, icon: 'bolt' },
]

const scenes = [
  { id: 'dashboard', label: 'Dashboard', icon: 'grid_view' },
  { id: 'mobile', label: 'App', icon: 'smartphone' },
  { id: 'typography', label: 'Type', icon: 'text_fields' }
]

const defaultPalette = [
  { role: 'Primary', name: 'Electric Indigo', hex: '#6366F1', usage: 'Main Actions' },
  { role: 'Secondary', name: 'Midnight', hex: '#312E81', usage: 'Navigation' },
  { role: 'Accent', name: 'Neon Mint', hex: '#34D399', usage: 'Highlights' },
  { role: 'Surface', name: 'Dark Void', hex: '#09090B', usage: 'Backgrounds' },
  { role: 'Text', name: 'Starlight', hex: '#FFFFFF', usage: 'Content' }
]

const palette = ref(JSON.parse(JSON.stringify(defaultPalette)))

function getLuminance(hex) {
  const rgb = parseInt(hex.slice(1), 16);
  const r = ((rgb >> 16) & 0xff) / 255;
  const g = ((rgb >> 8) & 0xff) / 255;
  const b = ((rgb >> 0) & 0xff) / 255;

  const a = [r, g, b].map(v => {
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
}

const contrastData = computed(() => {
  const surf = palette.value[3].hex;
  const text = palette.value[4].hex;

  const lum1 = getLuminance(surf);
  const lum2 = getLuminance(text);
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);

  const ratio = (brightest + 0.05) / (darkest + 0.05);
  const rounded = ratio.toFixed(2);

  let label = 'Fail';
  let colorClass = 'text-red-500 bg-red-500/10 border-red-500/20';
  let barColor = 'bg-red-500';

  if (ratio >= 7) {
    label = 'AAA Pass';
    colorClass = 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20';
    barColor = 'bg-emerald-500';
  } else if (ratio >= 4.5) {
    label = 'AA Pass';
    colorClass = 'text-blue-500 bg-blue-500/10 border-blue-500/20';
    barColor = 'bg-blue-500';
  } else if (ratio >= 3) {
    label = 'AA Large';
    colorClass = 'text-amber-500 bg-amber-500/10 border-amber-500/20';
    barColor = 'bg-amber-500';
  }

  const percent = Math.min((ratio / 21) * 100, 100);

  return { ratio: rounded, label, colorClass, barColor, percent };
})

function hslToHex(h, s, l) {
  l /= 100;
  const a = s * Math.min(l, 1 - l) / 100;
  const f = n => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`.toUpperCase();
}

function randomize() {
  const baseHue = Math.floor(Math.random() * 360);

  const isDarkTheme = Math.random() > 0.3;

  const primaryHex = hslToHex(baseHue, 80, 60);
  const secondaryHex = hslToHex((baseHue + 20) % 360, 60, isDarkTheme ? 30 : 80);
  const accentHex = hslToHex((baseHue + 180) % 360, 95, 65);

  const surfaceHex = isDarkTheme
    ? hslToHex(baseHue, 15, 6)
    : hslToHex(baseHue, 10, 97);

  const textHex = isDarkTheme ? '#FFFFFF' : '#09090B';

  palette.value[0].hex = primaryHex;
  palette.value[1].hex = secondaryHex;
  palette.value[2].hex = accentHex;
  palette.value[3].hex = surfaceHex;
  palette.value[4].hex = textHex;
}

function reset() {
  palette.value = JSON.parse(JSON.stringify(defaultPalette))
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
@import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500&display=swap');

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease, transform 0.4s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: scale(0.98);
}

.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(161, 161, 170, 0.2);
  border-radius: 99px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(161, 161, 170, 0.4);
}

.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>